import { useEffect, useRef } from "react";
import {
  type ActionFunctionArgs,
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useFetcher, useLoaderData } from "@remix-run/react";
import { Mail } from "lucide-react";
import crypto from "crypto";
import {
  getSupabaseWithSessionHeaders,
  supabaseAdmin,
} from "../lib/supabase_server";
import Header from "../components/header";
import Footer from "../components/Footer";
import Button from "../components/theme/Button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { enforceRateLimit, RATE_LIMIT } from "../lib/rateLimit.server";

const CONTACT_INBOX = process.env.CONTACT_NOTIFICATION_EMAIL ??
  "bulkbuyplace@gmail.com";

export async function action({ request }: ActionFunctionArgs) {
  const limited = enforceRateLimit(request, {
    prefix: "contact",
    ...RATE_LIMIT.publicForm,
  });
  if (limited) return limited;

  const formData = await request.formData();
  const name = String(formData.get("name") ?? "").trim();
  const email = String(formData.get("email") ?? "").trim();
  const message = String(formData.get("message") ?? "").trim();

  if (!name || name.length > 200) {
    return json({ ok: false, error: "Please enter a valid name." }, {
      status: 400,
    });
  }
  if (
    !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 320
  ) {
    return json({ ok: false, error: "Please enter a valid email address." }, {
      status: 400,
    });
  }
  if (!message || message.length > 10_000) {
    return json(
      { ok: false, error: "Please enter a message (max 10,000 characters)." },
      { status: 400 },
    );
  }

  const { error } = await supabaseAdmin.from("EmailOutbox").insert({
    to_email: CONTACT_INBOX,
    template: "contact_form_inquiry",
    subject: `Contact: ${name}`,
    payload: {
      name,
      from_email: email,
      message,
    },
    send_after: new Date().toISOString(),
    idempotency_key: `contact:${crypto.randomUUID()}`,
    status: "QUEUED",
  });

  if (error) {
    console.error("Contact form EmailOutbox insert failed:", error);
    return json(
      {
        ok: false,
        error: "Could not send your message. Please try again later.",
      },
      { status: 500 },
    );
  }

  return json({ ok: true as const });
}

export async function loader({ request }: LoaderFunctionArgs) {
  const { headers, supabase, session } = await getSupabaseWithSessionHeaders({
    request,
  });
  let userName = "";
  let isAdmin = false;
  if (session) {
    const { data } = await supabase
      .from("Users")
      .select("first_name, admin")
      .eq("id", session.user.id)
      .single();
    userName = data?.first_name ?? "";
    isAdmin = Boolean(data?.admin);
  }
  return json(
    { userId: session?.user?.id ?? "", userName, isAdmin },
    { headers },
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: "Contact Us - BulkBuyPlace" },
    {
      name: "description",
      content: "Get in touch with the BulkBuyPlace team.",
    },
  ];
};

export default function ContactPage() {
  const { userId, userName, isAdmin } = useLoaderData<typeof loader>();
  const { toast } = useToast();
  const fetcher = useFetcher<typeof action>();
  const formRef = useRef<HTMLFormElement>(null);
  const pendingSubmitRef = useRef(false);
  const isSubmitting = fetcher.state !== "idle";

  useEffect(() => {
    if (fetcher.state === "submitting") pendingSubmitRef.current = true;
    if (
      fetcher.state !== "idle" || !pendingSubmitRef.current || !fetcher.data
    ) {
      return;
    }
    pendingSubmitRef.current = false;
    if (fetcher.data.ok) {
      toast({
        title: "Message sent",
        description: "We'll get back to you within 24 hours.",
        variant: "success",
      });
      formRef.current?.reset();
    } else if ("error" in fetcher.data && fetcher.data.error) {
      toast({
        title: "Could not send",
        description: fetcher.data.error,
        variant: "destructive",
      });
    }
  }, [fetcher.state, fetcher.data, toast]);

  return (
    <div className="flex flex-col min-h-[100dvh] bg-gray-50">
      <Header userId={userId} userName={userName} isAdmin={isAdmin} />

      <main className="flex-1 pt-16">
        <section className="px-4 py-16 md:py-24">
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-10">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600 mb-4">
                <Mail className="h-5 w-5" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Contact us</h1>
              <p className="mt-2 text-gray-500">
                Have a question or need help? We typically respond within 24
                hours.
              </p>
            </div>

            <form
              ref={formRef}
              method="post"
              className="space-y-5 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
              onSubmit={(e) => {
                e.preventDefault();
                fetcher.submit(new FormData(e.currentTarget), {
                  method: "post",
                  action: "/contact",
                });
              }}
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message">Message</Label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  placeholder="How can we help?"
                  required
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>

              <Button
                type="submit"
                mode="primary"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-400">
              You can also reach us at{" "}
              <a
                href="mailto:bulkbuyplace@gmail.com"
                className="text-orange-600 hover:underline"
              >
                bulkbuyplace@gmail.com
              </a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
